/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback, useEffect, useRef, useState } from "react";
import { devLog } from "@/utils/devLog";
import { useStreamAttacher } from "@/hooks/useStreamAttacher";

/**
 * Hook principal de lecture audio.
 * Combine élément audio, attachement HLS/MP3, et état de lecture.
 */
export function useRadioPlayer() {
  const audioRef = useRef(null);
  const [currentId, setCurrentId] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [volume, setVolume] = useState(0.8);
  const [muted, setMuted] = useState(false);

  // Sync volume <-> élément audio
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = muted ? 0 : volume;
    }
  }, [volume, muted]);

  const handlePlayStart = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    audio
      .play()
      .then(() => setIsPlaying(true))
      .catch((err) => {
        devLog("play() failed", err);
        setIsPlaying(false);
        setIsLoading(false);
      });
  }, []);

  const handlePlayError = useCallback(() => {
    setIsLoading(false);
    setIsPlaying(false);
  }, []);

  const { attachStream, destroyHls } = useStreamAttacher(
    audioRef,
    handlePlayStart,
    handlePlayError
  );

  const resetAudio = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    try {
      audio.pause();
      audio.removeAttribute("src");
      audio.load();
    } catch (err) {
      devLog("audio reset failed", err);
    }
    destroyHls();
  }, [destroyHls]);

  const playRadio = useCallback(
    (radio) => {
      const audio = audioRef.current;
      if (!audio) return;
      // Toggle si même radio
      if (currentId === radio.id && !audio.paused) {
        audio.pause();
        setIsPlaying(false);
        return;
      }
      resetAudio();
      audio.volume = muted ? 0 : volume;
      setCurrentId(radio.id);
      setIsLoading(true);
      attachStream(radio);
    },
    [currentId, volume, muted, resetAudio, attachStream]
  );

  const togglePlay = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    if (audio.paused) {
      setIsLoading(true);
      audio
        .play()
        .then(() => setIsPlaying(true))
        .catch(() => setIsLoading(false));
      return;
    }
    audio.pause();
    setIsPlaying(false);
  }, []);

  return {
    audioRef,
    currentId,
    isPlaying,
    isLoading,
    volume,
    muted,
    setVolume,
    setMuted,
    setIsPlaying,
    setIsLoading,
    playRadio,
    togglePlay,
  };
}
